import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { getVenues } from '../../../api/VenuesAPI';
import { getUsers } from '../../../api/UsersAPI';
import { createGame } from '../../../api/GamesAPI';
import { handleApiError } from '../../../api/risapi';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';

const GameSetupForm = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [gameData, setGameData] = useState({
        game_type: 'Internal',
        venue_id: null,
        game_date: '',
        overs: '20',
        allow_last_man_stands: false, // Add new state for the rule
        teams: [{ name: 'Team A', players: [] }, { name: 'Team B', players: [] }],
    });
    const [venues, setVenues] = useState([]);
    const [clubMembers, setClubMembers] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        getVenues({ paginate: false }, (res) => {
            if (res.venues) setVenues(res.venues.map(v => ({ value: v.id, label: v.name })));
        });
        getUsers({ paginate: false }, (res) => {
            if (res.users) setClubMembers(res.users.data || res.users);
        });
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        // Handle checkbox input for the new rule
        const inputValue = type === 'checkbox' ? checked : value;
        setGameData(prev => ({ ...prev, [name]: inputValue }));
    };
    
    const handleTeamNameChange = (index, newName) => {
        const newTeams = [...gameData.teams];
        newTeams[index].name = newName;
        setGameData(prev => ({ ...prev, teams: newTeams }));
    };

    const handleAddPlayer = (teamIndex, player) => {
        const newTeams = [...gameData.teams];
        // Prevent adding the same player twice to the same team
        if (!newTeams[teamIndex].players.some(p => p.player_name === player.player_name)) {
            newTeams[teamIndex].players.push(player);
            setGameData(prev => ({ ...prev, teams: newTeams }));
        }
    };

    const handleRemovePlayer = (teamIndex, playerIndex) => {
        const newTeams = [...gameData.teams];
        newTeams[teamIndex].players.splice(playerIndex, 1);
        setGameData(prev => ({ ...prev, teams: newTeams }));
    };
    
    const nextStep = () => setStep(s => s + 1);
    const prevStep = () => setStep(s => s - 1);

    const handleSubmit = () => {
        setIsLoading(true);
        const submissionData = { ...gameData, venue_id: gameData.venue_id.value };
        createGame(submissionData, (response) => {
            setIsLoading(false);
            if (response.game) {
                navigate('/club_portal/games', { state: { message: 'Game scheduled successfully!' } });
            } else {
                handleApiError(response, setErrors);
            }
        });
    };

    return (
        <main>
            <div className="container-fluid">
                <AppBreadCrumb title="Schedule New Game" />
                <div className="card">
                    <div className="card-body">
                        {/* Progress Bar */}
                        <div className="progress mb-4" style={{ height: '10px' }}>
                            <div className="progress-bar" role="progressbar" style={{ width: `${(step / 3) * 100}%` }} aria-valuenow={(step / 3) * 100} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>

                        {/* Step 1: Game Details */}
                        {step === 1 && (
                            <div>
                                <h5>Step 1: Game Details</h5>
                                <div className="row">
                                    <div className="col-md-6 mb-3"><label className="form-label">Game Type</label><select name="game_type" className="form-select" value={gameData.game_type} onChange={handleInputChange}><option>Internal</option><option>Friendly</option><option>Tournament</option></select></div>
                                    <div className="col-md-6 mb-3"><label className="form-label">Venue</label><Select options={venues} value={gameData.venue_id} onChange={(v) => setGameData(p => ({ ...p, venue_id: v }))} /></div>
                                    <div className="col-md-6 mb-3"><label className="form-label">Date & Time</label><input type="datetime-local" name="game_date" className="form-control" value={gameData.game_date} onChange={handleInputChange} /></div>
                                    <div className="col-md-6 mb-3"><label className="form-label">Overs</label><input type="number" name="overs" className="form-control" value={gameData.overs} onChange={handleInputChange} /></div>
                                    
                                    {/* --- "Last Man Stands" Checkbox --- */}
                                    <div className="col-12 mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="allow_last_man_stands"
                                                id="lastManStandsCheck"
                                                checked={gameData.allow_last_man_stands}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label" htmlFor="lastManStandsCheck">
                                                Allow Last Man to Bat Alone (Last Man Stands)
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-end"><button className="btn btn-primary" onClick={nextStep}>Next: Define Teams</button></div>
                            </div>
                        )}

                        {/* Step 2: Define Teams */}
                        {step === 2 && (
                             <div>
                                <h5>Step 2: Define Teams</h5>
                                {gameData.teams.map((team, index) => (
                                    <div key={index} className="mb-3"><label className="form-label">Team {index + 1} Name</label><input type="text" className="form-control" value={team.name} onChange={(e) => handleTeamNameChange(index, e.target.value)} /></div>
                                ))}
                                <div className="d-flex justify-content-between"><button className="btn btn-secondary" onClick={prevStep}>Previous</button><button className="btn btn-primary" onClick={nextStep}>Next: Set Squads</button></div>
                            </div>
                        )}

                        {/* Step 3: Set Squads */}
                        {step === 3 && (
                             <div>
                                <h5>Step 3: Set Squads</h5>
                                <div className="row">
                                {gameData.teams.map((team, teamIndex) => (
                                    <div key={teamIndex} className="col-md-6">
                                        <div className="p-3 border rounded mb-3">
                                            <h6>{team.name}</h6>
                                            <ul className="list-group mb-2">
                                                {team.players.length > 0 ? team.players.map((p, pIndex) => (
                                                    <li key={pIndex} className="list-group-item d-flex justify-content-between align-items-center py-1">
                                                        {p.player_name}
                                                        <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => handleRemovePlayer(teamIndex, pIndex)}>&times;</button>
                                                    </li>
                                                )) : <li className="list-group-item text-muted">No players added yet.</li>}
                                            </ul>
                                            <PlayerSelect clubMembers={clubMembers} onAddPlayer={(p) => handleAddPlayer(teamIndex, p)} />
                                        </div>
                                    </div>
                                ))}
                                </div>
                                <div className="d-flex justify-content-between mt-4"><button className="btn btn-secondary" onClick={prevStep}>Previous</button><button className="btn btn-primary" onClick={handleSubmit} disabled={isLoading}>{isLoading ? 'Scheduling...' : 'Schedule Game'}</button></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

// Helper component for selecting players
const PlayerSelect = ({ clubMembers, onAddPlayer }) => {
    const [selectedMember, setSelectedMember] = useState(null);
    const [guestName, setGuestName] = useState('');

    const handleAddMember = () => {
        if (!selectedMember) return;
        onAddPlayer({ user_id: selectedMember.value, player_name: selectedMember.label });
        setSelectedMember(null);
    };

    const handleAddGuest = () => {
        if (!guestName.trim()) return;
        onAddPlayer({ user_id: null, player_name: guestName });
        setGuestName('');
    };

    const memberOptions = clubMembers.map(m => ({ value: m.id, label: m.name }));

    return (
        <div>
            <div className="mb-2">
                <Select options={memberOptions} value={selectedMember} onChange={setSelectedMember} placeholder="Select from club members..." />
                <button className="btn btn-sm btn-outline-primary mt-1 w-100" onClick={handleAddMember}>Add Member</button>
            </div>
            <div className="mb-2">
                <input type="text" className="form-control" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Or add guest player name..." />
                <button className="btn btn-sm btn-outline-secondary mt-1 w-100" onClick={handleAddGuest}>Add Guest</button>
            </div>
        </div>
    );
};

export default GameSetupForm;

